<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  rows: {
    type: Number,
    default: 6,
  },
  variables: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

const TRIGGER_CHAR = String.fromCharCode(64);

const textareaRef = ref(null);
const internalValue = ref(props.modelValue);
const showSuggestions = ref(false);
const selectedIndex = ref(-1);
const searchTerm = ref('');
const cursorStart = ref(0);
const cursorEnd = ref(0);

watch(
  () => props.modelValue,
  value => {
    if (value !== internalValue.value) internalValue.value = value;
  }
);

watch(internalValue, value => {
  emit('update:modelValue', value);
});

const variableOptions = computed(() => props.variables || []);

const filteredVariables = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  if (!term) return variableOptions.value;

  return variableOptions.value.filter(option => {
    const label = (option.label || '').toLowerCase();
    const value = (option.value || '').toLowerCase();
    return label.includes(term) || value.includes(term);
  });
});

const resetSuggestions = () => {
  showSuggestions.value = false;
  searchTerm.value = '';
  selectedIndex.value = -1;
};

const syncCursor = () => {
  const textarea = textareaRef.value;
  if (!textarea) return;
  cursorStart.value = textarea.selectionStart || 0;
  cursorEnd.value = textarea.selectionEnd || 0;
};

const evaluateTrigger = () => {
  const text = internalValue.value || '';
  const start = cursorStart.value;
  const textBeforeCursor = text.slice(0, start);
  const triggerIdx = textBeforeCursor.lastIndexOf(TRIGGER_CHAR);

  if (triggerIdx < 0) {
    resetSuggestions();
    return;
  }

  const textAfterTrigger = textBeforeCursor.slice(triggerIdx + 1);
  if (textAfterTrigger.includes(' ') || textAfterTrigger.includes('\n')) {
    resetSuggestions();
    return;
  }

  searchTerm.value = textAfterTrigger;
  showSuggestions.value = true;
  selectedIndex.value = filteredVariables.value.length ? 0 : -1;
};

const replaceTriggerText = variableValue => {
  const text = internalValue.value || '';
  const start = cursorStart.value;
  const end = cursorEnd.value;
  const textBeforeCursor = text.slice(0, start);
  const triggerIdx = textBeforeCursor.lastIndexOf(TRIGGER_CHAR);

  if (triggerIdx < 0) return;

  const nextValue = `${text.slice(0, triggerIdx)}${variableValue}${text.slice(end)}`;
  internalValue.value = nextValue;

  requestAnimationFrame(() => {
    const caret = triggerIdx + variableValue.length;
    if (textareaRef.value) {
      textareaRef.value.focus();
      textareaRef.value.setSelectionRange(caret, caret);
      cursorStart.value = caret;
      cursorEnd.value = caret;
    }
  });

  resetSuggestions();
};

const insertAtCursor = variableValue => {
  const text = internalValue.value || '';
  const start = cursorStart.value;
  const end = cursorEnd.value;

  const nextValue = `${text.slice(0, start)}${variableValue}${text.slice(end)}`;
  internalValue.value = nextValue;

  requestAnimationFrame(() => {
    const caret = start + variableValue.length;
    if (textareaRef.value) {
      textareaRef.value.focus();
      textareaRef.value.setSelectionRange(caret, caret);
      cursorStart.value = caret;
      cursorEnd.value = caret;
    }
  });
};

const onInput = event => {
  internalValue.value = event.target.value;
  syncCursor();
  evaluateTrigger();
};

const onKeydown = event => {
  if (!showSuggestions.value) return;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    if (!filteredVariables.value.length) return;
    selectedIndex.value =
      (selectedIndex.value + 1) % filteredVariables.value.length;
    return;
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault();
    if (!filteredVariables.value.length) return;
    selectedIndex.value =
      (selectedIndex.value - 1 + filteredVariables.value.length) %
      filteredVariables.value.length;
    return;
  }

  if (event.key === 'Enter' || event.key === 'Tab') {
    if (
      selectedIndex.value < 0 ||
      !filteredVariables.value[selectedIndex.value]
    )
      return;
    event.preventDefault();
    replaceTriggerText(filteredVariables.value[selectedIndex.value].value);
    return;
  }

  if (event.key === 'Escape') {
    event.preventDefault();
    resetSuggestions();
  }
};

const onBlur = () => {
  setTimeout(() => {
    resetSuggestions();
  }, 120);
};

const onClickOutside = event => {
  if (!textareaRef.value) return;
  if (
    textareaRef.value === event.target ||
    textareaRef.value.contains(event.target)
  )
    return;
  resetSuggestions();
};

window.addEventListener('click', onClickOutside);
onUnmounted(() => {
  window.removeEventListener('click', onClickOutside);
});
</script>

<template>
  <div class="space-y-2">
    <label v-if="label" class="text-sm font-medium text-n-slate-12">
      {{ label }}
    </label>

    <div class="relative">
      <textarea
        ref="textareaRef"
        :value="internalValue"
        :placeholder="placeholder"
        :rows="rows"
        class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 text-sm text-n-slate-12 placeholder:text-n-slate-10 px-3 py-3 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand resize-y"
        @input="onInput"
        @keydown="onKeydown"
        @click="syncCursor"
        @keyup="syncCursor"
        @focus="syncCursor"
        @blur="onBlur"
      />

      <div
        v-if="showSuggestions && filteredVariables.length"
        class="absolute left-0 right-0 mt-1 z-20 border border-n-weak rounded-lg bg-n-slate-2 shadow-lg max-h-44 overflow-y-auto"
      >
        <button
          v-for="(option, index) in filteredVariables"
          :key="option.value"
          type="button"
          class="w-full text-left px-3 py-2 border-b border-n-weak last:border-b-0"
          :class="
            index === selectedIndex ? 'bg-n-blue-3' : 'hover:bg-n-alpha-3'
          "
          @mousedown.prevent="replaceTriggerText(option.value)"
        >
          <span class="block text-sm font-medium text-n-slate-12">{{
            option.label
          }}</span>
          <span class="block text-xs text-n-slate-11">{{ option.value }}</span>
        </button>
      </div>
    </div>

    <p class="text-xs text-n-slate-11">
      {{ t('CAMPAIGN.BUHOTEXT.FORM.MESSAGE.VARIABLES_TRIGGER_HELP') }}
    </p>

    <div class="flex flex-wrap gap-2">
      <button
        v-for="option in variableOptions"
        :key="option.value"
        type="button"
        class="px-2.5 py-1.5 rounded-md border border-n-blue-6 bg-n-blue-3 text-n-blue-12 text-xs font-medium hover:bg-n-blue-4"
        @click="insertAtCursor(option.value)"
      >
        {{ option.label }}
      </button>
    </div>

    <p v-if="error" class="text-xs text-n-ruby-9">
      {{ error }}
    </p>
  </div>
</template>
